// frontend/src/pages/SurveyList.jsx
import React, { useState, useEffect, useContext, useMemo } from "react";
import { FaPrint, FaTimes, FaUserShield } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { authHeaders } from "../utils/authHeaders";
import SurveyAccessDialog, { SURVEY_ROLE_OPTIONS } from "../components/SurveyAccessDialog";
import "./SurveyList.css";

const API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.replace(/\/$/, "")) ||
  `${window.location.protocol}//${window.location.hostname}:3000/api`;

function isGpsValue(v) {
  return (
    v &&
    typeof v === "object" &&
    Number.isFinite(Number(v.latitude)) &&
    Number.isFinite(Number(v.longitude))
  );
}

function formatGpsValue(v) {
  if (!isGpsValue(v)) return "";
  const lat = Number(v.latitude).toFixed(6);
  const lng = Number(v.longitude).toFixed(6);
  const accuracy = Number.isFinite(Number(v.accuracy))
    ? `, akurasi ${Math.round(Number(v.accuracy))} m`
    : "";
  return `${lat}, ${lng}${accuracy}`;
}

const SurveyList = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {survey, questions}
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [publishingId, setPublishingId] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [locatingPreviewQuestion, setLocatingPreviewQuestion] = useState(null);
  const [copiedShareId, setCopiedShareId] = useState(null);
  const [accessSurvey, setAccessSurvey] = useState(null);

  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  useEffect(() => {
    fetchSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function asJsonOrThrow(res) {
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const t = await res.text();
      throw new Error(`Unexpected response (not JSON): ${t.slice(0, 120)}Ã¢â‚¬Â¦`);
    }
    const j = await res.json();
    if (!res.ok || !j.success) {
      throw new Error(j?.message || `HTTP ${res.status}`);
    }
    return j;
  }

  function tryParseArray(v) {
    try {
      const j = JSON.parse(v);
      return Array.isArray(j) ? j : [];
    } catch {
      return [];
    }
  }

  // ===== Date helpers =====
  const toMs = (v) => {
    if (!v) return null;
    const d = new Date(v);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  // Ambil "publish terakhir" dari field apa pun yang tersedia (robust)
  const getLastPublishedAt = (s) => {
    // pilih yang paling masuk akal dari backend Anda
    return (
      s.latest_published_at ||
      s.last_published_at ||
      s.published_at ||
      s.publishedAt ||
      null
    );
  };

  // Ambil status published (robust)
  const isPublished = (s) => {
    // Jika Anda pakai survey_versions, biasanya last_published_at / latest_published_at akan terisi
    // Jika Anda pakai is_published, bisa juga.
    return Boolean(
      s.is_published === 1 ||
        s.is_published === true ||
        s.published === 1 ||
        s.published === true ||
        getLastPublishedAt(s)
    );
  };

  const getUserDisplayName = (name, id) => {
    const clean = String(name || "").trim();
    if (clean) return clean;
    return id ? `User #${id}` : "-";
  };

  const getChannelLabel = (channel) => {
    return channel === "whatsapp_simple" ? "WhatsApp Survey" : "Web Form";
  };

  const getShareUrl = (surveyId) => `${window.location.origin}/survey/${encodeURIComponent(surveyId)}`;

  const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "");
    input.style.position = "fixed";
    input.style.top = "-9999px";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  };

  const handleCopyShareLink = async (surveyId) => {
    const url = getShareUrl(surveyId);
    try {
      await copyToClipboard(url);
      setCopiedShareId(surveyId);
      window.setTimeout(() => {
        setCopiedShareId((cur) => (cur === surveyId ? null : cur));
      }, 1800);
    } catch (e) {
      console.error("Copy share link error:", e);
      window.prompt("Salin link survey berikut:", url);
    }
  };

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/surveys`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setSurveys(json.data || []);
    } catch (e) {
      console.error("Error fetching surveys:", e);
      alert("Failed to fetch surveys");
    } finally {
      setLoading(false);
    }
  };

  const openPreview = async (id) => {
    try {
      const res = await fetch(`${API}/surveys/${id}`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setPreviewAnswers({});
      setLocatingPreviewQuestion(null);
      setPreview({ survey: json.survey, questions: json.questions || [] });
    } catch (e) {
      console.error("Preview error:", e);
      alert("Gagal memuat detail survey untuk preview.");
    }
  };

  const closePreview = () => {
    setPreview(null);
    setPreviewAnswers({});
    setLocatingPreviewQuestion(null);
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const getPrintOptions = (question) => {
    if (Array.isArray(question?.options)) return question.options;
    return question?.options ? tryParseArray(question.options) : [];
  };

  const renderPrintQuestion = (question, index) => {
    const type = String(question?.type || "text");
    const options = getPrintOptions(question);
    const required = question?.required ? '<span class="required">*</span>' : "";
    let answerArea = '<div class="answer-lines"><span></span><span></span></div>';

    if (type === "select_one" || type === "select_multiple") {
      const marker = type === "select_one" ? "circle" : "square";
      answerArea = `<div class="print-options">${options
        .map((option) => `<div><span class="${marker}"></span>${escapeHtml(option)}</div>`)
        .join("")}</div>`;
    } else if (type === "date") {
      answerArea = '<div class="single-line">Tanggal: ____ / ____ / ________</div>';
    } else if (type === "gps") {
      answerArea = '<div class="single-line">Latitude: ____________________ &nbsp;&nbsp; Longitude: ____________________</div>';
    } else if (type === "media") {
      answerArea = '<div class="single-line">Media/lampiran: ________________________________________________</div>';
    } else if (type === "calculation") {
      answerArea = `<div class="calculation-box"><span>Formula: ${escapeHtml(question.calculation || "-")}</span><strong>Hasil: ____________________</strong></div>`;
    }

    return `
      <section class="print-question">
        <div class="question-heading">
          <strong>${index + 1}. ${escapeHtml(question?.question_text || "Pertanyaan")}${required}</strong>
          <span>${escapeHtml(type.replace(/_/g, " "))}</span>
        </div>
        ${answerArea}
      </section>`;
  };

  const handlePrintPdf = async (id) => {
    const printWindow = window.open("", "_blank", "width=980,height=760");
    if (!printWindow) {
      alert("Popup diblokir browser. Izinkan popup untuk mencetak survey.");
      return;
    }

    printWindow.opener = null;
    printWindow.document.write("<!doctype html><title>Menyiapkan survey...</title><p style='font-family:Arial;padding:24px'>Menyiapkan dokumen...</p>");
    setPrintingId(id);

    try {
      const res = await fetch(`${API}/surveys/${id}`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      const surveyData = json.survey || {};
      const questions = Array.isArray(json.questions) ? json.questions : [];
      const createdAt = surveyData.created_at
        ? new Date(surveyData.created_at).toLocaleString("id-ID")
        : "-";

      printWindow.document.open();
      printWindow.document.write(`<!doctype html>
        <html lang="id">
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>${escapeHtml(surveyData.title || "Survey")}</title>
            <style>
              @page { size: A4; margin: 14mm; }
              * { box-sizing: border-box; }
              body { margin: 0; color: #172033; font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.4; }
              .document-head { border-bottom: 2px solid #205f37; padding-bottom: 12px; margin-bottom: 18px; }
              h1 { margin: 0 0 6px; color: #174b29; font-size: 22pt; letter-spacing: 0; }
              .description { margin: 0 0 10px; color: #475569; white-space: pre-wrap; }
              .meta { display: flex; gap: 20px; color: #64748b; font-size: 9pt; }
              .print-question { break-inside: avoid; border: 1px solid #dbe4dd; border-radius: 6px; padding: 12px 14px; margin-bottom: 10px; }
              .question-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; }
              .question-heading strong { font-size: 11.5pt; }
              .question-heading > span { color: #64748b; font-size: 8.5pt; text-transform: capitalize; white-space: nowrap; }
              .required { color: #b42318; margin-left: 3px; }
              .answer-lines { display: grid; gap: 13px; padding-top: 6px; }
              .answer-lines span, .single-line { min-height: 24px; border-bottom: 1px solid #9ca3af; }
              .print-options { display: grid; gap: 8px; }
              .print-options > div { display: flex; align-items: center; gap: 8px; }
              .circle, .square { width: 14px; height: 14px; border: 1.5px solid #64748b; flex: 0 0 14px; }
              .circle { border-radius: 50%; }
              .square { border-radius: 2px; }
              .calculation-box { display: flex; justify-content: space-between; gap: 18px; padding: 9px 11px; background: #f1f7f2; border: 1px solid #c8ddcd; border-radius: 4px; }
              .calculation-box span { color: #476153; }
              footer { margin-top: 16px; color: #64748b; font-size: 8.5pt; text-align: right; }
              @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            <header class="document-head">
              <h1>${escapeHtml(surveyData.title || "Survey")}</h1>
              ${surveyData.description ? `<p class="description">${escapeHtml(surveyData.description)}</p>` : ""}
              <div class="meta"><span>Dibuat: ${escapeHtml(createdAt)}</span><span>Jumlah pertanyaan: ${questions.length}</span></div>
            </header>
            <main>${questions.map(renderPrintQuestion).join("")}</main>
            <footer>Sahabat Kakao - Form Survey</footer>
            <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 250); });</script>
          </body>
        </html>`);
      printWindow.document.close();
    } catch (error) {
      console.error("Print survey error:", error);
      printWindow.close();
      alert("Gagal menyiapkan survey untuk dicetak.");
    } finally {
      setPrintingId(null);
    }
  };

  const handleEdit = (id) => {
    navigate(`/survey-builder/${id}`, { state: { mode: "edit", editId: id } });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus survey ini?")) return;
    try {
      const res = await fetch(`${API}/surveys/${id}`, {
        method: "DELETE",
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      if (!json.success) throw new Error(json.message || "Gagal menghapus survey");
      fetchSurveys();
    } catch (e) {
      console.error(e);
      alert("Gagal menghapus survey");
    }
  };

  // =========================
  // Publish / Republish
  // =========================
  const handlePublish = async (id, modeLabel = "publish") => {
    const msg =
      modeLabel === "republish"
        ? "Publish ulang survey ini? Versi baru akan tampil untuk user baru, sementara versi lama yang sedang berjalan tetap bisa diselesaikan."
        : "Publish survey ini? Setelah publish, survey tersedia di Lapor Yuk sesuai pengaturan aksesnya.";

    if (!window.confirm(msg)) return;

    setPublishingId(id);
    try {
      // pattern A: endpoint publish cukup idempotent.
      // untuk republish, backend Anda (pakai versioning) akan membuat versi baru dan set published_at di survey_versions.
      const res = await fetch(`${API}/surveys/${id}/publish`, {
        method: "PUT",
        headers: authHeaders(user, {
          "Content-Type": "application/json",
          Accept: "application/json",
        }),
        credentials: "include",
        // body TIDAK wajib; tetap aman kalau backend Anda tidak butuh.
        body: JSON.stringify({}),
      });

      const json = await asJsonOrThrow(res);
      if (!json.success) throw new Error(json.message || "Gagal publish survey");

      await fetchSurveys();
      alert("Survey berhasil dipublish. Sekarang tersedia di menu Lapor Yuk!");
    } catch (e) {
      console.error("Publish error:", e);
      alert("Gagal publish survey. Cek endpoint /surveys/:id/publish di backend dan response-nya.");
    } finally {
      setPublishingId(null);
    }
  };

  // =========================
  // Logic evaluator (SHOW/HIDE) for preview
  // =========================
  function getLogicArray(q) {
    const raw = q?.logic ?? q?.logics ?? q?.rules;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") return tryParseArray(raw);
    return [];
  }

  function getAnswer(qNo) {
    return previewAnswers[String(qNo)];
  }

  function getConditionQuestionNo(cond, questions = []) {
    const key = cond?.question_key;
    if (key) {
      const idx = questions.findIndex((q) => String(q.client_key || "") === String(key));
      if (idx >= 0) return idx + 1;
    }
    return Number(cond?.question_id);
  }

  function evalCondition(cond, questions = []) {
    const qNo = getConditionQuestionNo(cond, questions);
    const op = cond?.operator || "equals";
    const expected = cond?.value;
    const actual = getAnswer(qNo);

    const asNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    if (op === "equals") {
      if (Array.isArray(actual)) return actual.includes(String(expected));
      return String(actual ?? "") === String(expected ?? "");
    }
    if (op === "not_equals") {
      if (Array.isArray(actual)) return !actual.includes(String(expected));
      return String(actual ?? "") !== String(expected ?? "");
    }
    if (op === "contains") {
      if (Array.isArray(actual)) return actual.includes(String(expected));
      return String(actual ?? "").toLowerCase().includes(String(expected ?? "").toLowerCase());
    }
    if (op === "greater") {
      const a = asNum(actual);
      const b = asNum(expected);
      if (a === null || b === null) return false;
      return a > b;
    }
    if (op === "less") {
      const a = asNum(actual);
      const b = asNum(expected);
      if (a === null || b === null) return false;
      return a < b;
    }
    return false;
  }

  function computeVisibleForQuestion(q, questions = []) {
    const rules = getLogicArray(q);
    const showRules = rules.filter((r) => r?.then?.action === "show");
    const hideRules = rules.filter((r) => r?.then?.action === "hide");

    let visible = showRules.length > 0 ? false : true;

    if (showRules.length > 0) {
      visible = showRules.some((r) => evalCondition(r.if, questions));
    }
    if (hideRules.some((r) => evalCondition(r.if, questions))) {
      visible = false;
    }
    return visible;
  }

  const previewVisibility = useMemo(() => {
    if (!preview?.questions?.length) return {};
    const vis = {};
    preview.questions.forEach((q, idx) => {
      const qNo = idx + 1;
      vis[qNo] = computeVisibleForQuestion(q, preview.questions);
    });
    return vis;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, previewAnswers]);

  const setAnswer = (qNo, value) => {
    setPreviewAnswers((prev) => ({ ...prev, [String(qNo)]: value }));
  };

  const toggleMulti = (qNo, optionValue) => {
    setPreviewAnswers((prev) => {
      const key = String(qNo);
      const cur = Array.isArray(prev[key]) ? prev[key] : [];
      const v = String(optionValue);
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { ...prev, [key]: next };
    });
  };

  const capturePreviewGps = (qNo) => {
    if (!navigator.geolocation) {
      alert("Browser/perangkat ini belum mendukung pengambilan lokasi GPS.");
      return;
    }

    setLocatingPreviewQuestion(qNo);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = pos.coords;
        setAnswer(qNo, {
          latitude: c.latitude,
          longitude: c.longitude,
          accuracy: c.accuracy,
          altitude: c.altitude,
          altitude_accuracy: c.altitudeAccuracy,
          heading: c.heading,
          speed: c.speed,
          captured_at: new Date(pos.timestamp || Date.now()).toISOString(),
        });
        setLocatingPreviewQuestion(null);
      },
      (err) => {
        setLocatingPreviewQuestion(null);
        const message =
          err.code === err.PERMISSION_DENIED
            ? "Izin lokasi ditolak. Aktifkan izin lokasi pada browser/perangkat."
            : err.code === err.TIMEOUT
            ? "Pengambilan lokasi terlalu lama. Coba lagi di area dengan sinyal GPS lebih baik."
            : "Gagal mengambil lokasi GPS. Pastikan GPS/lokasi perangkat aktif.";
        alert(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      }
    );
  };

  const renderQuestion = (q, idx) => {
    const qNo = idx + 1;
    const req = q.required ? " *" : "";

    const opts = q.options
      ? Array.isArray(q.options)
        ? q.options
        : tryParseArray(q.options)
      : [];

    const isVisible = previewVisibility[qNo] !== false;
    if (!isVisible) return null;

    const answer = getAnswer(qNo);

    return (
      <div key={q.id || idx} className="preview-question">
        <h4>
          {qNo}. {q.question_text}
          {req}
        </h4>

        {q.type === "select_one" && (
          <div className="choice-group">
            {opts.map((o, i) => (
              <label key={i} className="choice">
                <input
                  type="radio"
                  name={`q${qNo}`}
                  checked={String(answer ?? "") === String(o)}
                  onChange={() => setAnswer(qNo, String(o))}
                />
                {o}
              </label>
            ))}
          </div>
        )}

        {q.type === "select_multiple" && (
          <div className="choice-group">
            {opts.map((o, i) => (
              <label key={i} className="choice">
                <input
                  type="checkbox"
                  name={`q${qNo}_${i}`}
                  checked={Array.isArray(answer) ? answer.includes(String(o)) : false}
                  onChange={() => toggleMulti(qNo, String(o))}
                />
                {o}
              </label>
            ))}
          </div>
        )}

        {q.type === "text" && (
          <input
            className="field"
            type="text"
            placeholder="Your answer"
            value={answer ?? ""}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "number" && (
          <input
            className="field"
            type="number"
            placeholder="Your answer"
            value={answer ?? ""}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "decimal" && (
          <input
            className="field"
            type="number"
            step="0.01"
            placeholder="Your answer"
            value={answer ?? ""}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "date" && (
          <input
            className="field"
            type="date"
            value={answer ?? ""}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "gps" && (
          <div className="gps-capture">
            <button
              type="button"
              className="preview-btn-soft"
              onClick={() => capturePreviewGps(qNo)}
              disabled={locatingPreviewQuestion === qNo}
            >
              {locatingPreviewQuestion === qNo ? "Mengambil lokasi..." : isGpsValue(answer) ? "Ambil Ulang Lokasi" : "Ambil Lokasi GPS"}
            </button>
            {isGpsValue(answer) ? (
              <div className="gps-result">
                <strong>Lokasi tersimpan</strong>
                <span>{formatGpsValue(answer)}</span>
              </div>
            ) : (
              <div className="gps-hint">Tekan tombol untuk mengambil lokasi perangkat saat ini.</div>
            )}
          </div>
        )}

        {q.type === "media" && (
          <input
            className="field"
            type="file"
            onChange={(e) => setAnswer(qNo, e.target.files?.[0]?.name || "")}
          />
        )}

        {q.calculation && (
          <div className="calc-hint">
            Kalkulasi: <code>{q.calculation}</code>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="survey-list">
        <div>Loading surveys...</div>
      </div>
    );
  }

  return (
    <div className="survey-list">
      <div className="list-head">
        <h1>Survey List</h1>
        <button className="create-btn" onClick={() => navigate("/survey-builder")}>
          Create New Survey
        </button>
      </div>

      {surveys.length === 0 ? (
        <div className="empty">Belum ada survey.</div>
      ) : (
        <ul className="surveys">
          {surveys.map((s) => {
            const published = isPublished(s);
            const lastPublishedAt = getLastPublishedAt(s);

            // penting: ini butuh backend kirim updated_at & last published date
            const updatedMs = toMs(s.updated_at || s.updatedAt || s.last_updated_at);
            const publishedMs = toMs(lastPublishedAt);

            // kalau sudah published, tapi ada update setelah publish Ã¢â€ â€™ perlu republish
            const needsRepublish =
              Boolean(published) &&
              Boolean(updatedMs && publishedMs) &&
              updatedMs > publishedMs;

            // kalau published tapi backend belum kirim tanggal Ã¢â€ â€™ tetap izinkan republish manual (safe)
            const canRepublishEvenWithoutDates = published && (!updatedMs || !publishedMs);

            const isUpToDate = published && !needsRepublish && !canRepublishEvenWithoutDates;

            const badgeText = !published
              ? "Draft"
              : needsRepublish
              ? "Needs Republish"
              : "Published";

            const badgeClass = !published
              ? "draft"
              : needsRepublish
              ? "republish"
              : "published";

            const publishBtnLabel = !published
              ? "Publish"
              : needsRepublish || canRepublishEvenWithoutDates
              ? "Publish Ulang"
              : "Published";

            const publishBtnTitle = !published
              ? "Publish survey agar bisa diakses user di Lapor Yuk!"
              : needsRepublish
              ? "Ada perubahan setelah publish. Publish ulang untuk versi baru."
              : canRepublishEvenWithoutDates
              ? "Publish ulang (backend belum kirim info waktu publish/update)."
              : "Survey sudah published & up-to-date";

            const publishDisabled =
              publishingId === s.id || (!(!published) && isUpToDate); // disable hanya jika up-to-date

            return (
              <li key={s.id} className="survey-card">
                <div className="row-top">
                  <div className="title">{s.title}</div>
                  <div className="badge-row">
                    <span className={`badge channel ${s.survey_channel === "whatsapp_simple" ? "wa" : "web"}`}>
                      {getChannelLabel(s.survey_channel)}
                    </span>
                    <span className={`badge ${badgeClass}`}>{badgeText}</span>
                  </div>
                </div>

                {s.description && <div className="desc">{s.description}</div>}
                <div className="survey-access-summary">
                  <FaUserShield aria-hidden="true" />
                  <span>Akses: {s.allowed_roles == null ? "Semua orang" : s.allowed_roles.map((value) => SURVEY_ROLE_OPTIONS.find((option) => option.value === value)?.label || value).join(", ") || "Admin"}</span>
                </div>

                <div className="meta">
                  <span>Created: {new Date(s.created_at).toLocaleString()}</span>
                  <span> - by {getUserDisplayName(s.created_by_name, s.created_by)}</span>
                  {lastPublishedAt ? (
                    <span> - Published: {new Date(lastPublishedAt).toLocaleString()}</span>
                  ) : null}
                  {s.updated_at ? (
                    <span>
                      {" "}
                      - Updated: {new Date(s.updated_at).toLocaleString()} by{" "}
                      {getUserDisplayName(s.updated_by_name || s.created_by_name, s.updated_by || s.created_by)}
                    </span>
                  ) : null}
                </div>

                <div className="actions">
                  <button onClick={() => openPreview(s.id)}>Preview</button>
                  <button
                    type="button"
                    className="print-pdf-btn"
                    onClick={() => handlePrintPdf(s.id)}
                    disabled={printingId === s.id}
                    title="Cetak atau simpan survey sebagai PDF"
                  >
                    <FaPrint aria-hidden="true" />
                    {printingId === s.id ? "Menyiapkan..." : "Print PDF"}
                  </button>
                  <button onClick={() => handleEdit(s.id)}>Edit</button>
                  {isAdmin && <button type="button" className="survey-access-btn" onClick={() => setAccessSurvey(s)}><FaUserShield aria-hidden="true" /> Akses Role</button>}

                  {published && (
                    <button type="button" onClick={() => handleCopyShareLink(s.id)}>
                      {copiedShareId === s.id ? "Link Copied" : "Copy Link"}
                    </button>
                  )}

                  {isAdmin && (
                    <button
                      className="publish-btn"
                      onClick={() =>
                        handlePublish(s.id, published ? "republish" : "publish")
                      }
                      disabled={publishDisabled}
                      title={publishBtnTitle}
                    >
                      {publishingId === s.id ? "Publishing..." : publishBtnLabel}
                    </button>
                  )}

                  <button onClick={() => handleDelete(s.id)}>Hapus</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {accessSurvey && <SurveyAccessDialog survey={accessSurvey} user={user} api={API} onClose={() => setAccessSurvey(null)} onSaved={(allowedRoles) => {
        setSurveys((current) => current.map((item) => item.id === accessSurvey.id ? { ...item, allowed_roles: allowedRoles } : item));
        setAccessSurvey(null);
      }} />}

      {preview && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-head">
              <div>
                <h3 className="preview-title">{preview.survey.title}</h3>
                {preview.survey.description && (
                  <p className="preview-desc">{preview.survey.description}</p>
                )}
              </div>
              <button
                type="button"
                className="close"
                onClick={closePreview}
                aria-label="Tutup preview survey"
                title="Tutup"
              >
                <FaTimes aria-hidden="true" focusable="false" />
                <span className="preview-close-legacy" aria-hidden="true">
                Ã¢Å“â€¢
                </span>
              </button>
            </div>

            <div className="preview-questions">
              {preview.questions.length === 0 ? (
                <em>Tidak ada pertanyaan di survey ini.</em>
              ) : (
                preview.questions.map((q, i) => renderQuestion(q, i))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyList;

